import { useTranslation } from 'react-i18next';
import type { AssessmentRunsFormProps } from '../shared/AssessmentRunsFormContainer';
import AssessmentRunsFormContainer from '../shared/AssessmentRunsFormContainer';
import { CreateAssessmentRunsMutation } from './CreateAssessmentRuns.graphql';
import type { CreateAssessmentRunsMutationVariables } from './CreateAssessmentRuns.graphql';
import client from '@/apolloClient';
import type { ProgramListItemDataFragment } from '@/gql/graphql';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

export interface CreateAssessmentRunsProps {
    program: ProgramListItemDataFragment;
    onClose: () => void;
    onSuccess: () => void;
}

const CreateAssessmentRuns = ({ program, onClose, onSuccess }: CreateAssessmentRunsProps) => {
    const { t } = useTranslation('programManagementPage');

    const handleSubmit: AssessmentRunsFormProps['onSubmit'] = async formData => {
        try {
            const variables: CreateAssessmentRunsMutationVariables = {
                input: {
                    programId: program.id,
                    groupId: formData.groupId,
                    assessmentRuns: formData.assessmentRuns,
                },
            };

            const { data } = await client.mutate({
                mutation: CreateAssessmentRunsMutation,
                variables,
            });

            if (data?.createAssessmentRuns?.__typename === 'CreateAssessmentRunsSuccessfulResponse') {
                if (onSuccess) {
                    onSuccess();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('assessmentRuns.notifications.successTitle'),
                    description: t('assessmentRuns.notifications.successDescription'),
                });
                return;
            }

            if (data?.createAssessmentRuns?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.createAssessmentRuns.fields,
                };
                showErrorNotification({
                    message: t('assessmentRuns.notifications.errorTitle'),
                    description: error.fields?.[0]?.message || t('assessmentRuns.notifications.errorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error creating assessment runs');
        } catch {
            throw new Error('An unexpected error occurred while creating assessment runs');
        }
    };
    return <AssessmentRunsFormContainer mode="create" onSubmit={handleSubmit} onClose={onClose} program={program} />;
};

export default CreateAssessmentRuns;
